// Peças da agenda usadas na tela Agenda e no Início.
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import { addDaysIso, fmtIsoFull, fmtIsoLong, plural, relDay, todayIso } from '../lib/format';
import type { AgendaItem, AgendaPessoa, RestaurantRow } from '../lib/types';
import { Button, Field, Sheet, useConfirm } from './ui';
import { AutoTextarea } from './fields';
import { useToast } from './feedback';
import { useStartVisit } from './StartVisit';

export const STATUS_LABEL = { agendada: 'Agendada', realizada: 'Realizada', nao_realizada: 'Não realizada' } as const;

const invalidate = (qc: ReturnType<typeof useQueryClient>, tid: string) => {
  qc.invalidateQueries({ queryKey: ['agenda', tid] });
  qc.invalidateQueries({ queryKey: ['home', tid] });
};

/** Ações de um agendamento (iniciar/registrar, remarcar, não foi feita, excluir). */
export function useAgendaActions(tid: string) {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const { start, busy } = useStartVisit(tid);
  const patch = useMutation({
    mutationFn: (o: { id: string; body: object }) => api.patch(`/api/t/${tid}/agenda/${o.id}`, o.body),
    onSuccess: () => invalidate(qc, tid),
    onError: (e) => toast(errMsg(e), 'err'),
  });
  const remove = async (it: AgendaItem) => {
    const ok = await confirm.ask({
      title: 'Excluir da agenda?',
      message: `${it.restaurantNome} · ${fmtIsoLong(it.data)}${it.hora ? ` às ${it.hora}` : ''}. ${it.visitId ? 'A visita registrada continua no sistema.' : ''}`,
      cta: 'Excluir', danger: true,
    });
    if (!ok) return;
    try {
      await api.del(`/api/t/${tid}/agenda/${it.id}`);
      invalidate(qc, tid);
      toast('Excluído da agenda');
    } catch (e) {
      toast(errMsg(e), 'err');
    }
  };
  /** Começa a visita pelo agendamento; dia que já passou vira o dia da visita. */
  const registrar = (it: AgendaItem) => start(it.restaurantId, { agendaId: it.id, ...(it.data < todayIso() ? { data: it.data } : {}) });
  const naoFeita = (it: AgendaItem) => patch.mutate({ id: it.id, body: { status: 'nao_realizada' } }, { onSuccess: () => toast('Marcada como não realizada') });
  const desfazer = (it: AgendaItem) => patch.mutate({ id: it.id, body: { status: 'agendada' } });
  return { registrar, naoFeita, desfazer, remove, busy, confirmNode: confirm.node };
}

/** Um agendamento com as ações que fazem sentido para o dia e a situação dele. */
export function AgendaCard({ tid, it, hoje, showPerson, showDay, onEdit, actions }: {
  tid: string; it: AgendaItem; hoje: string; showPerson?: boolean; showDay?: boolean;
  onEdit: (it: AgendaItem) => void; actions: ReturnType<typeof useAgendaActions>;
}) {
  const passou = it.data < hoje;
  const cls = it.status === 'realizada' ? 'done' : it.status === 'nao_realizada' ? 'miss' : '';
  const pill =
    it.status === 'realizada' ? { bg: 'var(--accent-soft)', fg: 'var(--resolved)', t: 'Realizada' }
      : it.status === 'nao_realizada' ? { bg: 'var(--nc-soft)', fg: 'var(--nc-text)', t: 'Não realizada' }
        : it.visitStatus === 'em_andamento' ? { bg: 'var(--accent-soft)', fg: 'var(--accent-dark)', t: 'Em andamento' }
          : passou ? { bg: 'var(--persist-soft)', fg: 'var(--persist-text)', t: 'Sem registro' } : null;
  return (
    <div className={`ag-item ${cls}`}>
      <div className="hora">{it.hora ?? '—'}</div>
      <div className="flex1" style={{ minWidth: 0 }}>
        <div className="row gap-6 wrap" style={{ alignItems: 'center' }}>
          <div className="wrapline" style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3 }}>{it.restaurantNome}</div>
          {pill && <span className="status-pill" style={{ background: pill.bg, color: pill.fg }}>{pill.t}</span>}
        </div>
        <div className="small" style={{ marginTop: 2 }}>
          {showDay ? `${relDay(it.data, hoje)}${showPerson ? ' · ' : ''}` : ''}{showPerson ? it.userNome : ''}
        </div>
        {it.observacao && <div className="wrapline" style={{ fontSize: 12.5, color: 'var(--text-3)', marginTop: 5, whiteSpace: 'pre-wrap' }}>{it.observacao}</div>}
        <div className="row gap-12 wrap" style={{ marginTop: 9, alignItems: 'center' }}>
          {it.visitId && it.visitStatus === 'finalizada' ? (
            <Link to={`/t/${tid}/relatorio/${it.visitId}`} className="link-btn u" style={{ fontWeight: 600 }}>Ver relatório nº {it.visitNumero} →</Link>
          ) : it.visitId && it.visitStatus === 'em_andamento' ? (
            <Link to={`/t/${tid}/visita/${it.visitId}`} className="btn sm dark">Continuar visita →</Link>
          ) : it.status === 'agendada' && it.data <= hoje ? (
            <Button size="sm" variant="dark" loading={actions.busy === it.id} onClick={() => actions.registrar(it)}>
              {passou ? 'Sim, fiz · registrar' : 'Iniciar visita'}
            </Button>
          ) : null}
          {it.status === 'agendada' && passou && !it.visitId && <button className="link-btn u" onClick={() => actions.naoFeita(it)}>Não foi feita</button>}
          {it.status === 'nao_realizada' && <button className="link-btn u" onClick={() => onEdit(it)}>Remarcar</button>}
          {it.status !== 'realizada' && <button className="link-btn u muted" onClick={() => onEdit(it)}>Editar</button>}
          <button className="link-btn u muted" onClick={() => void actions.remove(it)}>Excluir</button>
        </div>
      </div>
    </div>
  );
}

/** "Você fez estas visitas?" — agendamentos de dias anteriores (ou de hoje depois das 19h) sem registro. */
export function ConfirmBlock({ tid, items, hoje, onEdit, actions, highlight }: {
  tid: string; items: AgendaItem[]; hoje: string; onEdit: (it: AgendaItem) => void; actions: ReturnType<typeof useAgendaActions>; highlight?: boolean;
}) {
  if (!items.length) return null;
  return (
    <section className="card ag-ask" style={highlight ? { boxShadow: '0 0 0 3px var(--persist-border)' } : undefined}>
      <h2 className="h2" style={{ color: 'var(--persist-text)' }}>{items.length === 1 ? 'Você fez esta visita?' : 'Você fez estas visitas?'}</h2>
      <div className="small" style={{ margin: '3px 0 12px' }}>
        Se fez, registre agora para entrar no sistema (a data fica a do agendamento). Se não deu, marque como não feita ou remarque.
      </div>
      <div className="stack gap-8">
        {items.map((it) => <AgendaCard key={it.id} tid={tid} it={it} hoje={hoje} showDay onEdit={onEdit} actions={actions} />)}
      </div>
    </section>
  );
}

const REPETIR: Array<[string, string]> = [['', 'Não repetir'], ['semana', 'Toda semana'], ['2semanas', 'A cada 2 semanas'], ['mes', 'Todo mês']];

/** Criar ou editar um agendamento. */
export function AgendaForm({ tid, open, onClose, item, dia, pessoas, meId, podeTodos, pessoaPadrao }: {
  tid: string; open: boolean; onClose: () => void; item: AgendaItem | null; dia: string;
  pessoas: AgendaPessoa[]; meId: string; podeTodos: boolean; pessoaPadrao?: string;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const rq = useQuery({ queryKey: ['restaurants', tid, ''], queryFn: () => api.get<RestaurantRow[]>(`/api/t/${tid}/restaurants`), enabled: open });
  const [restaurantId, setRestaurantId] = useState('');
  const [data, setData] = useState(dia);
  const [hora, setHora] = useState('');
  const [obs, setObs] = useState('');
  const [userId, setUserId] = useState(meId);
  const [repetir, setRepetir] = useState('');
  const [vezes, setVezes] = useState(4);
  const [filtro, setFiltro] = useState('');

  useEffect(() => {
    if (!open) return;
    setRestaurantId(item?.restaurantId ?? '');
    setData(item?.data ?? dia);
    setHora(item?.hora ?? '');
    setObs(item?.observacao ?? '');
    setUserId(item?.userId ?? pessoaPadrao ?? meId);
    setRepetir('');
    setVezes(4);
    setFiltro('');
  }, [open, item, dia, meId, pessoaPadrao]);

  const pessoa = pessoas.find((p) => p.userId === userId);
  const restaurantes = useMemo(() => {
    const all = rq.data ?? [];
    const acc = pessoa?.restaurantIds;
    return all.filter((r) => (!acc || acc === 'all' || acc.includes(r.id)) && (!filtro || r.nome.toLowerCase().includes(filtro.toLowerCase())));
  }, [rq.data, pessoa, filtro]);

  const save = useMutation({
    mutationFn: () => {
      const body = { restaurantId, data, hora: hora || null, observacao: obs.trim() ? obs : null, userId };
      return item
        ? api.patch(`/api/t/${tid}/agenda/${item.id}`, { ...body, ...(item.status === 'nao_realizada' && data !== item.data ? { status: 'agendada' } : {}) })
        : api.post<{ datas: string[] }>(`/api/t/${tid}/agenda`, { ...body, ...(repetir ? { repetir: { cada: repetir, vezes } } : {}) });
    },
    onSuccess: (r) => {
      invalidate(qc, tid);
      const n = (r as { datas?: string[] } | null)?.datas?.length ?? 1;
      toast(item ? 'Agendamento atualizado' : n > 1 ? `${n} visitas agendadas` : `Visita agendada para ${relDay(data)}`);
      onClose();
    },
    onError: (e) => toast(errMsg(e), 'err'),
  });

  if (!open || !data) return null;
  return (
    <Sheet open={open} onClose={onClose} title={item ? 'Editar agendamento' : 'Agendar visita'} subtitle={fmtIsoLong(data)} wide>
      <form onSubmit={(e) => { e.preventDefault(); if (!restaurantId) return toast('Escolha o restaurante', 'err'); save.mutate(); }}>
        {podeTodos && pessoas.length > 1 && (
          <Field label="Quem vai fazer a visita">
            <div className="row gap-6 wrap">
              {pessoas.map((p) => (
                <button key={p.userId} type="button" className={`chip toggle ${userId === p.userId ? 'on' : ''}`} onClick={() => setUserId(p.userId)}>
                  {p.userId === meId ? 'Eu' : p.nome.split(' ').slice(0, 2).join(' ')}
                </button>
              ))}
            </div>
          </Field>
        )}
        <div className="field">
          <span className="label">Restaurante</span>
          {(rq.data?.length ?? 0) > 6 && <input className="input sm" style={{ marginBottom: 8 }} placeholder="Buscar restaurante" value={filtro} onChange={(e) => setFiltro(e.target.value)} />}
          <div className="stack gap-6" style={{ maxHeight: 220, overflowY: 'auto' }}>
            {restaurantes.map((r) => (
              <button key={r.id} type="button" className="item" style={{ padding: '10px 12px', borderColor: restaurantId === r.id ? 'var(--accent)' : undefined, background: restaurantId === r.id ? 'var(--accent-soft)' : undefined }} onClick={() => setRestaurantId(r.id)}>
                <span className="flex1 wrapline" style={{ fontSize: 13.5, fontWeight: restaurantId === r.id ? 600 : 500 }}>{r.nome}</span>
                {restaurantId === r.id && <span style={{ color: 'var(--accent)', fontWeight: 700 }}>✓</span>}
              </button>
            ))}
            {rq.data && !restaurantes.length && <div className="small">Nenhum restaurante {pessoa && pessoa.userId !== meId ? `liberado para ${pessoa.nome.split(' ')[0]}` : 'encontrado'}.</div>}
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="Dia"><input className="input" type="date" value={data} onChange={(e) => e.target.value && setData(e.target.value)} /></Field>
          <Field label="Hora (opcional)"><input className="input" type="time" value={hora} onChange={(e) => setHora(e.target.value)} /></Field>
        </div>
        <div className="row gap-6 wrap" style={{ margin: '-4px 0 12px' }}>
          {[0, 1, 2, 7].map((n) => {
            const d = addDaysIso(todayIso(), n);
            return <button key={n} type="button" className={`chip toggle sm ${data === d ? 'on' : ''}`} onClick={() => setData(d)}>{n === 7 ? 'Daqui 1 semana' : relDay(d)}</button>;
          })}
        </div>
        {!item && (
          <Field label="Repetir">
            <div className="row gap-6 wrap">
              {REPETIR.map(([v, l]) => <button key={v} type="button" className={`chip toggle sm ${repetir === v ? 'on' : ''}`} onClick={() => setRepetir(v)}>{l}</button>)}
            </div>
            {repetir && (
              <div className="row gap-6 wrap" style={{ marginTop: 8, alignItems: 'center' }}>
                <span className="small">Quantas vezes:</span>
                {[4, 8, 12, 26].map((n) => <button key={n} type="button" className={`chip toggle sm ${vezes === n ? 'on' : ''}`} onClick={() => setVezes(n)}>{n}</button>)}
              </div>
            )}
          </Field>
        )}
        <Field label="Observação (opcional)">
          <AutoTextarea placeholder="Ex.: levar checklist, falar com a gerente, conferir câmara fria" value={obs} onChange={(e) => setObs(e.target.value)} />
        </Field>
        <Button variant="dark" size="lg" block type="submit" loading={save.isPending} disabled={!restaurantId}>
          {item ? 'Salvar' : repetir ? `Agendar ${plural(vezes, 'visita', 'visitas')}` : `Agendar para ${relDay(data)} · ${fmtIsoFull(data)}`}
        </Button>
      </form>
    </Sheet>
  );
}
